import { node } from "prop-types";
import { forwardRef, useMemo } from "react";

import styled from "styled-components";

import { motion } from "framer-motion";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrCard from "UI/FnrCard";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrMovingDiv from "UI/FnrMovingDiv";
import FnrSlides from "UI/FnrSlides";
import { useFnrUploadButtonContext } from "UI/FnrUploadButton/FnrUploadButton";
import {
  useIsUploading,
  useProgressGradientString,
} from "UI/FnrUploadButton/stateHooks";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";

import WithValueDisplay from "./WithValueDisplay";
import WithoutValueDisplay from "./WithoutValueDisplay";

const addShade = ({
  $addShade,
  theme: {
    colors: { fnGrey },
  },
}) => {
  if ($addShade) return `background-color: ${fnGrey}33`;
  return "";
};

const paddingLeft = ({ $compact }) =>
  $compact ? "margin-left: -4px; padding-left: 4px;" : "";

const SMotionDiv = styled(motion.div)`
  margin-left: -4px;
  padding-left: 4px;
  border-radius: 4px;
`;

const SFnrCard = styled(FnrCard)`
  ${({ $compact }) => ($compact ? "width: 100%;" : "")}
  ${paddingLeft}
  border-radius: ${({ $compact }) => ($compact ? "6px" : "10px")};
  border-color: ${({
    theme: {
      colors: { fnGrey },
    },
  }) => fnGrey};
  ${addShade}
`;

const slidesConfig = [
  {
    key: "withoutValue",
    value: WithoutValueDisplay,
  },
  {
    key: "withValue",
    value: WithValueDisplay,
  },
];

const SFnrButtonWrapper = styled(FnrButtonWrapper)`
  width: 100%;
  ${({ $hide }) => ($hide ? "width: 0px; height: 0px; overflow: clip;" : "")}
`;

const SDiv = styled("div")`
  width: 100%;
`;

const SFnrButtonWrapperRelay = forwardRef(({ children, ...props }, ref) => {
  const { readOnly, hide } = useFnrUploadButtonContext();
  const isUploading = useIsUploading();

  if (readOnly) return <SDiv ref={ref}>{children}</SDiv>;
  return (
    <SFnrButtonWrapper $hide={hide} ref={ref} {...props} disabled={isUploading}>
      {children}
    </SFnrButtonWrapper>
  );
});

SFnrButtonWrapperRelay.propTypes = { children: node };

const VisileContents = () => {
  const {
    containerRef,
    href,
    hasValue,
    inputRef,
    isHoverFocused,
    compact,
    noShadeOnHover,
    icon,
    color,
    readOnly,
  } = useFnrUploadButtonContext();
  const gradientString = useProgressGradientString();
  const isUploading = useIsUploading();

  const handleClick = () => {
    if (href && hasValue) return; // allow native code to navigate to the download.
    if (isUploading) return; // don't encourage the user to start another upload.
    inputRef.current.click();
  };

  const showUploadIcon = useMemo(() => {
    if (readOnly && !icon) return false; // We still want to show any custom icons that are passed in
    return !compact || !hasValue;
  }, [compact, hasValue, readOnly, icon]);

  const contentGap = useMemo(() => {
    if (showUploadIcon) return compact ? 8 : 12;
    return 0;
  }, [compact, showUploadIcon]);

  return (
    <SFnrButtonWrapperRelay
      ref={containerRef}
      onClick={handleClick}
      data-cy="FnrUploadButton_Container"
    >
      <SFnrCard
        noGradient
        $addShade={isHoverFocused && !hasValue && !noShadeOnHover}
        $compact={compact}
        variant={compact ? "hidden" : "container"}
      >
        <SMotionDiv
          animate={{
            backgroundImage: gradientString,
            transition: { duration: 0.75 },
          }}
        >
          <FnrHorizontalGap gap={contentGap} fullHeight>
            <FnrMovingDiv widthMotion>
              {showUploadIcon && (
                <FnrContainedIcon
                  data-cy="FnrUploadButton_icon"
                  icon={icon || "fa-upload fal"}
                  color={color}
                  noPadding
                />
              )}
            </FnrMovingDiv>
            <FnrSlides
              fullWidth
              components={slidesConfig}
              activeKey={hasValue ? "withValue" : "withoutValue"}
              clip={false}
              axis="y"
            />
          </FnrHorizontalGap>
        </SMotionDiv>
      </SFnrCard>
    </SFnrButtonWrapperRelay>
  );
};

export default VisileContents;
